import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Input, Text, SegmentedButtons } from '@Sage/hoi-poi-ui';

import ModalHoi from 'components/ModalHoi';
import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import { REG_EX_EMAIL } from 'constants/Constants';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { sendTestEmail } from 'services/MailingService';

import './styles.scss';

const CampaignPreviewTestModal = memo(
    ({
        isOpen,
        onRequestClose,
        from,
        subject,
        previewText,
        htmlPreview,
        defaultRecipient,
        onSendTestEmail,
    }) => {
        const [previewMode, setPreviewMode] = useState('web');
        const [recipient, setRecipient] = useState(defaultRecipient || '');
        const [recipientError, setRecipientError] = useState('');
        const [isLoading, setIsLoading] = useState(false);

        const emailRegex = useMemo(() => new RegExp(REG_EX_EMAIL.source, 'i'), []);

        useEffect(() => {
            if (!isOpen) {
                return;
            }

            setRecipient(defaultRecipient || '');
            setRecipientError('');
        }, [isOpen, defaultRecipient]);

        const handleClose = useCallback(() => {
            setRecipientError('');
            onRequestClose?.();
        }, [onRequestClose]);

        const handlePreviewModeChange = useCallback((mode) => {
            setPreviewMode(mode);
        }, []);

        const handleRecipientChange = useCallback(
            (value) => {
                setRecipient(value);
                if (recipientError) {
                    setRecipientError('');
                }
            },
            [recipientError],
        );

        const validateRecipient = useCallback(() => {
            const normalized = recipient?.trim() || '';

            if (!normalized) {
                setRecipientError(getLiteral('helptext_required_field'));
                return false;
            }

            if (!emailRegex.test(normalized)) {
                setRecipientError(getLiteral('error_invalid_email_format'));
                return false;
            }

            setRecipientError('');
            return true;
        }, [emailRegex, recipient]);

        const handleSendTestEmail = useCallback(async () => {
            if (!validateRecipient()) {
                return;
            }

            const payload = {
                to: [recipient.trim()],
                from,
                subject,
                previewText,
                body: htmlPreview,
            };

            setIsLoading(true);
            sendTestEmail(payload)
                .then(() => {
                    successToast({
                        text: getLiteral('message_marketing_campaigns_test_email_sent'),
                    });
                    onSendTestEmail?.(normalizedRpayloadecipient);
                })
                .catch(() => {
                    errorToast({ text: getLiteral('error_generalerror') });
                })
                .finally(() => setIsLoading(false));
        }, [
            from,
            htmlPreview,
            onSendTestEmail,
            previewText,
            recipient,
            subject,
            validateRecipient,
        ]);

        return (
            <ModalHoi
                className="campaign-preview-test-modal"
                isOpen={isOpen}
                onRequestClose={handleClose}
                title={getLiteral('label_marketing_campaigns_preview_and_test')}
                useHeader
                useCornerClose
                size="full"
            >
                <div className="campaign-preview-test-modal__content">
                    <div className="campaign-preview-test-modal__preview-column">
                        <div className="campaign-preview-test-modal__preview__header">
                            <div className="campaign-preview-test-modal__header-left">
                                <div className="campaign-preview-test-modal__row">
                                    <Text type="body" bold>
                                        {`${getLiteral('label_from')}:`}
                                    </Text>
                                    <Text type="body">{from || '-'}</Text>
                                </div>
                                <div className="campaign-preview-test-modal__row">
                                    <Text type="body" bold>
                                        {`${getLiteral('label_subject')}:`}
                                    </Text>
                                    <Text type="body">{subject || '-'}</Text>
                                </div>
                            </div>
                            <div className="campaign-preview-test-modal__header-right">
                                <SegmentedButtons
                                    options={[
                                        { name: 'web', icon: 'desktop' },
                                        { name: 'mobile', icon: 'mobile' },
                                    ]}
                                    value={previewMode}
                                    onChange={handlePreviewModeChange}
                                />
                            </div>
                        </div>

                        <div
                            className={classNames('campaign-preview-test-modal__html-preview', {
                                'campaign-preview-test-modal__html-preview--mobile':
                                    previewMode === 'mobile',
                            })}
                        >
                            <HtmlPreviewFrame
                                html={htmlPreview}
                                title={getLiteral('label_marketing_campaigns_preview_and_test')}
                            />
                        </div>
                    </div>

                    <div className="campaign-preview-test-modal__form-column">
                        <Text type="h6">
                            {getLiteral('label_marketing_campaigns_preview_test_recipient_heading')}
                        </Text>
                        <Text type="body" className="campaign-preview-test-modal__description">
                            {getLiteral('label_marketing_campaigns_preview_test_recipient_desc')}
                        </Text>

                        <Input
                            isFullWidth
                            label={`${getLiteral('label_recipient')}*`}
                            value={recipient}
                            onChange={handleRecipientChange}
                            placeholder={getLiteral('placeholder_enter_email_address')}
                            error={recipientError}
                        />

                        <Button
                            className="campaign-preview-test-modal__send-test-email-button"
                            type="primary"
                            onClick={handleSendTestEmail}
                            isLoading={isLoading}
                        >
                            {getLiteral('action_marketing_campaigns_send_test_email')}
                        </Button>
                    </div>
                </div>
            </ModalHoi>
        );
    },
);

CampaignPreviewTestModal.displayName = 'CampaignPreviewTestModal';

CampaignPreviewTestModal.propTypes = {
    isOpen: PropTypes.bool,
    onRequestClose: PropTypes.func,
    from: PropTypes.string,
    subject: PropTypes.string,
    previewText: PropTypes.string,
    htmlPreview: PropTypes.string,
    defaultRecipient: PropTypes.string,
    onSendTestEmail: PropTypes.func,
};

export default CampaignPreviewTestModal;
